import type {
  Editor,
  TLAsset,
  TLAssetId,
  TLDocument,
  TLInstancePresence,
  TLPageId,
  TLRecord,
  TLStoreWithStatus,
} from '@digitalsamba/tldraw';
import { notification } from 'features/notifications/toast/notification';
import { UserId } from 'features/users/types';
import { selectLocalUser, selectUserById } from 'features/users/usersSlice';
import {
  whiteboardSettingsPushed,
  whiteboardSettingsReceived,
  whiteboardSettingsRemoved,
  whiteboardSettingsReset,
} from 'features/whiteboard/whiteboardSlice';
import i18n from 'i18n';
import { SignalingSocket } from 'services/signaling';
import { store as reduxStore } from 'store/store';
import { lazySingleton } from 'utils/lazySingleton';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { selectRoomId } from 'features/room/roomSlice';
import { e2eeEncrypt } from 'features/e2ee/utils/e2eeEncrypt';
import { getEncryptionKey } from 'utils/whiteboard/getEncryptionKey';
import {
  buildSettingRecord,
  ParsedSetting,
  splitRecords,
  splitRemovedIds,
  toSettings,
  WhiteboardRecord,
  WhiteboardRecordId,
  WhiteboardSettingKey,
} from 'utils/whiteboard/settings';
import { fileToBase64, SUPPORTED_IMAGE_MIME_TYPES } from 'utils/file';
import { encodeWhiteboardRecord } from 'utils/whiteboard/encodeWhiteboardRecord';
import { uploadWhiteboardImage } from 'utils/whiteboard/uploadWhiteboardImage';
import { getTldrawSync, loadTldraw } from './tldrawRuntime';
import { defaultAssetUrls, TLDrawLanguagesMap } from './assetUrls';

export type RecordId = TLRecord['id'];

export type RecordList = Record<RecordId, TLRecord>;

export interface WBRemoteUpdateData {
  changed: WhiteboardRecord[];
  removed: WhiteboardRecordId[];
}

export interface CursorUpdatePayload {
  id: string;
  userId: UserId;
  data: string;
}

type HandleImageUploadOptions = {
  file: File;
  fromSdk?: boolean;
  position?: {
    x: number;
    y: number;
  };
};

type PublishOptions = {
  remote?: boolean;
};

export type WhiteboardData = {
  id: string;
  name: string;
  shapes: WhiteboardRecord[];
};

const CHANGE_INTERVAL = 100;
const POINTER_CHANGE_INTERVAL = 100;

export interface WBCursorCoords {
  x: number;
  y: number;
}

export class BoardStateManager {
  whiteboardId: string | null = null;

  userId: string | null = null;

  intervalId: number = 0;

  pointerChangeIntervalId: number = 0;

  store: TLStoreWithStatus = {
    status: 'loading',
  };

  buffer: RecordList = {};

  removedBuffer: Record<string, boolean> = {};

  initialBuffer: Record<string, boolean> = {};

  localChangesPending = false;

  pointerCoords: {
    x: number;
    y: number;
  } = { x: 0, y: 0 };

  presenceIdByUserId: Record<UserId, TLInstancePresence['id']> = {};

  storedCoordsByUserId: Record<UserId, WBCursorCoords> = {};

  showPointers: boolean = true;

  editor: Editor | null = null;

  currentLocale: keyof typeof defaultAssetUrls.translations | null = null;

  shouldInitStore = false;

  // only a watched board flushes its buffer, so nothing can be sent while this is false
  isWatching = false;

  // one-shot: armed on a fresh room join (breakout onRoomJoinedSaga) and consumed on
  // editor mount, so auto zoom-to-fit fires on join but not when returning from a breakout
  allowAutoFitOnMount = false;

  private previousPointerCoords: {
    x: number;
    y: number;
  } = { x: 0, y: 0 };

  /**
   * Initialize the whiteboard.
   * This method is async because it needs to load TLDraw on first use.
   */
  init = async (data: WhiteboardData) => {
    await loadTldraw();

    this.shouldInitStore = true;

    this.whiteboardId = data.id;
    this.userId = selectLocalUser(reduxStore.getState())?.id ?? null;

    this.createStore();

    reduxStore.dispatch(whiteboardSettingsReset());

    if (data.shapes.length) {
      this.setState(data.shapes);
    }
  };

  reset = () => {
    this.store = {
      status: 'loading',
    };

    this.whiteboardId = null;
    this.userId = null;
    this.shouldInitStore = false;
    this.localChangesPending = false;
    this.buffer = {};
    this.removedBuffer = {};
    this.stop();

    reduxStore.dispatch(whiteboardSettingsReset());
  };

  createStore = () => {
    if (this.store.status !== 'loading') {
      if (this.shouldInitStore) {
        this.store = {
          status: 'loading',
        };

        this.buffer = {};
        this.removedBuffer = {};
        this.initialBuffer = {};
        this.localChangesPending = false;
        this.stop();
      } else {
        return;
      }
    }

    this.shouldInitStore = false;

    const tldraw = getTldrawSync();
    const { createTLStore, defaultShapeUtils, DocumentRecordType, PageRecordType } = tldraw;

    const store = createTLStore({ shapeUtils: defaultShapeUtils });

    store.put([
      DocumentRecordType.create({
        id: 'document:document' as TLDocument['id'],
      }),

      PageRecordType.create({
        id: 'page:page' as TLPageId,
        name: 'Page 1',
        index: 'a1',
      }),
    ]);

    store.listen(
      ({ changes }) => {
        Object.values(changes.added).forEach((record) => {
          this.insertShape(record.id, record);
        });

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.values(changes.updated).forEach(([_, record]) => {
          this.insertShape(record.id, record);
        });

        Object.values(changes.removed).forEach((record) => {
          this.removeShape(record.id);
        });
      },
      { source: 'user', scope: 'document' }
    );

    // old store will be gargbage collected, right? =);
    this.store = {
      store,
      status: 'synced-remote',
      connectionStatus: 'online',
    };
  };

  initEditor = (editor: Editor) => {
    this.editor = editor;

    if (this.currentLocale) {
      editor.user.updateUserPreferences({
        locale: this.currentLocale,
      });
    }
  };

  updateLocale = (locale: string) => {
    const mappedLocale = TLDrawLanguagesMap[locale];
    if (!mappedLocale) {
      return;
    }

    this.currentLocale = mappedLocale;

    if (this.editor) {
      this.editor.user.updateUserPreferences({
        locale: mappedLocale,
      });
    }
  };

  insertShape = (id: RecordId, shape: any) => {
    if (!this.initialBuffer[id]) {
      this.buffer[id] = shape;

      delete this.removedBuffer[id];

      this.localChangesPending = true;
    }

    delete this.initialBuffer[id];
  };

  removeShape = (id: RecordId) => {
    delete this.buffer[id];
    delete this.initialBuffer[id];

    this.removedBuffer[id] = true;

    this.localChangesPending = true;
  };

  watch = () => {
    this.isWatching = true;
    this.intervalId = window.setInterval(this.checkForChanges, CHANGE_INTERVAL);
    this.pointerChangeIntervalId = window.setInterval(
      this.updatePointerCoords,
      POINTER_CHANGE_INTERVAL
    );
  };

  stop = () => {
    this.isWatching = false;
    clearInterval(this.intervalId);
    clearInterval(this.pointerChangeIntervalId);
  };

  setState = (records: WhiteboardRecord[]) => {
    const { shapes, settings } = splitRecords(records);

    this.initialBuffer = shapes.reduce<Record<string, boolean>>((buffer, record) => {
      buffer[record.id] = true;
      return buffer;
    }, {});

    this.store.store?.put(shapes);

    this.publishSettings(settings);
  };

  setSetting = (key: WhiteboardSettingKey, value: string) => {
    const record = buildSettingRecord(key, value);

    // settings are not tldraw records; the buffer only serializes and sends them
    this.buffer[record.id as RecordId] = record as unknown as TLRecord;
    this.localChangesPending = true;
  };

  acceptRemoteChanges = (data: WBRemoteUpdateData) => {
    const { shapes, settings } = splitRecords(data.changed);
    const { recordIds, settingKeys } = splitRemovedIds(data.removed);

    this.store.store?.mergeRemoteChanges(() => {
      if (shapes.length) {
        this.store.store?.put(shapes);
      }
      if (recordIds.length) {
        this.store.store?.remove(recordIds);
      }
    });

    this.publishSettings(settings, { remote: true });

    if (settingKeys.length) {
      reduxStore.dispatch(whiteboardSettingsRemoved(settingKeys));
    }
  };

  updateRemotePointer = (data: { id: string; userId: UserId; coords: WBCursorCoords }) => {
    if (!this.store.store) {
      return;
    }

    this.storedCoordsByUserId[data.userId] = data.coords;

    if (!this.showPointers) {
      return;
    }

    const user = selectUserById(reduxStore.getState(), data.userId);

    if (!(user?.name && user?.avatarColor)) {
      return;
    }

    const tldraw = getTldrawSync();
    const { InstancePresenceRecordType } = tldraw;

    const presence = InstancePresenceRecordType.create({
      id: InstancePresenceRecordType.createId(data.userId),
      currentPageId: 'page:page' as TLPageId,
      userId: data.userId,
      userName: user.name,
      color: user.avatarColor,
      cursor: { x: data.coords.x, y: data.coords.y, type: 'default', rotation: 0 },
      lastActivityTimestamp: Date.now(),
    });

    this.presenceIdByUserId[data.userId] = presence.id;

    this.store.store.mergeRemoteChanges(() => {
      this.store.store?.put([presence]);
    });
  };

  removeRemotePointer = (id: UserId) => {
    if (this.store.store && this.presenceIdByUserId[id]) {
      this.store.store.mergeRemoteChanges(() => {
        this.store.store?.remove([this.presenceIdByUserId[id]]);
      });
    }
  };

  togglePointers = () => {
    if (this.showPointers) {
      Object.keys(this.presenceIdByUserId).forEach((key) => {
        this.removeRemotePointer(key);
      });

      this.showPointers = false;
    } else {
      this.showPointers = true;

      Object.entries(this.storedCoordsByUserId).forEach(([userId, coords]) => {
        this.updateRemotePointer({ id: this.whiteboardId!, userId, coords });
      });
    }

    return this.showPointers;
  };

  renamePointer = (id: UserId, name: string) => {
    if (!this.store.store || !this.showPointers) {
      return;
    }

    if (this.presenceIdByUserId[id]) {
      this.store.store.mergeRemoteChanges(() => {
        this.store.store?.update(this.presenceIdByUserId[id], (presence) => ({
          ...presence,
          userName: name,
        }));
      });
    }
  };

  setReadonly = (state: boolean) => {
    if (this.editor) {
      this.editor.updateInstanceState({ isReadonly: state });
    }
  };

  handleImageUpload = async ({ file, fromSdk, position }: HandleImageUploadOptions) => {
    if (!SUPPORTED_IMAGE_MIME_TYPES.includes(file.type)) {
      notification(i18n.t('notifications:wb_upload_file_type_unsupported'));
      return undefined;
    }

    let encryptionKey: CryptoKey | undefined;
    if (E2EEManager.e2eeEnabled) {
      const roomId = selectRoomId(reduxStore.getState());
      encryptionKey = getEncryptionKey(this.whiteboardId, roomId);
      if (!encryptionKey) {
        return undefined;
      }
    }

    const uploaded = await uploadWhiteboardImage(file, { encryptionKey });

    if (!uploaded) {
      return undefined;
    }

    const { url, file: uploadedFile } = uploaded;
    const mimeType = uploadedFile.type;

    const { AssetRecordType, getHashForString, MediaHelpers, isGifAnimated } = getTldrawSync();

    const dataUrl = await fileToBase64(uploadedFile);
    const size = await MediaHelpers.getImageSizeFromSrc(dataUrl);
    const assetId: TLAssetId = AssetRecordType.createId(getHashForString(url));
    const isAnimated = mimeType === 'image/gif' && (await isGifAnimated(uploadedFile));

    // use base64 file in case of E2EE
    const src = E2EEManager.e2eeEnabled ? dataUrl : url;

    const asset: TLAsset = AssetRecordType.create({
      id: assetId,
      type: 'image',
      typeName: 'asset',
      props: {
        name: uploadedFile.name,
        src,
        w: size.w,
        h: size.h,
        mimeType,
        isAnimated,
      },
      meta: E2EEManager.e2eeEnabled
        ? {
            // preserve the original url with the encrypted file
            src: url,
          }
        : undefined,
    });

    if (fromSdk && this.editor) {
      this.editor.createAssets([asset]);

      this.editor.createShape({
        type: 'image',
        x: position?.x ?? (window.innerWidth - size.w) / 2,
        y: position?.y ?? (window.innerHeight - size.h) / 2,
        props: {
          assetId,
          w: size.w,
          h: size.h,
        },
      });
    }

    return asset;
  };

  private publishSettings = (
    settings: ParsedSetting[],
    { remote = false }: PublishOptions = {}
  ) => {
    if (!settings.length) {
      return;
    }

    const applied = toSettings(settings);

    reduxStore.dispatch(
      remote ? whiteboardSettingsPushed(applied) : whiteboardSettingsReceived(applied)
    );
  };

  private flushBuffer = async () => {
    if (!this.localChangesPending) {
      return;
    }

    const buffer = { ...this.buffer };
    const removedBuffer = { ...this.removedBuffer };

    this.buffer = {};
    this.removedBuffer = {};
    this.localChangesPending = false;

    let encryptionKey: CryptoKey | undefined;

    if (E2EEManager.e2eeEnabled) {
      const roomId = selectRoomId(reduxStore.getState());

      encryptionKey = getEncryptionKey(this.whiteboardId, roomId);
      if (!encryptionKey) {
        return;
      }
    }

    const promises = Object.entries(buffer).map(async ([recordId, record]) => ({
      shapeId: recordId,
      data: await encodeWhiteboardRecord(record, encryptionKey),
    }));

    const changed = await Promise.all(promises);
    const removed = Object.keys(removedBuffer);

    SignalingSocket.send({
      event: 'updateWhiteboard',
      data: {
        id: this.whiteboardId,
        changed,
        removed,
      },
    });
  };

  private checkForChanges = () => {
    if (this.localChangesPending) {
      this.flushBuffer();
    }
  };

  private updatePointerCoords = async () => {
    if (
      this.pointerCoords.x !== this.previousPointerCoords.x ||
      this.pointerCoords.y !== this.previousPointerCoords.y
    ) {
      let encryptionKey: CryptoKey | undefined;

      if (E2EEManager.e2eeEnabled) {
        const roomId = selectRoomId(reduxStore.getState());

        encryptionKey = getEncryptionKey(this.whiteboardId, roomId);
        if (!encryptionKey) {
          return;
        }
      }

      const serializedData = JSON.stringify(this.pointerCoords);
      const data = encryptionKey
        ? await e2eeEncrypt(serializedData, encryptionKey)
        : serializedData;

      SignalingSocket.send({
        event: 'updateWhiteboardCursor',
        data: {
          id: this.whiteboardId,
          data,
        },
      });

      this.previousPointerCoords = this.pointerCoords;
    }
  };
}

export const board = lazySingleton<BoardStateManager>(() => new BoardStateManager());
